/**
 * Role Utilities - Standardized role checking across the application
 * 
 * STANDARD ROLE NAMES (see /docs/PATHKIT_ROLE_TAXONOMY.md for canonical reference):
 * - Backend stored values: 'systemAdmin', 'consultant', 'cfAdmin', 'billingAdmin', 'bdRep', 'companyAdmin', 'user'
 * - User-facing labels:    'System Admin', 'Consultant', 'Firm Admin', 'Billing Admin', 'Business Development Rep', 'Client Engagement Lead', 'Client Team Member'
 */

export type UserRole = 
  | 'systemAdmin'
  | 'consultant'
  | 'cfAdmin'
  | 'billingAdmin'
  | 'bdRep'
  | 'companyAdmin'
  | 'companyUser'  // Legacy, equivalent to 'user'
  | 'user'
  | 'client'; // Legacy, equivalent to 'user'

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: string; // Legacy single role field (deprecated)
  roles?: string[]; // New roles array (preferred)
  consultingFirmId?: string;
  companyId?: string;
  isAdmin?: boolean; // Base system-admin privilege flag (preserved during role switches)
  isCompanyAdmin?: boolean; // Legacy company admin flag
  isBillingAdmin?: boolean; // Legacy billing admin flag
  activeRole?: string; // Active role for view selection (determines current UI experience)
  baseRole?: string; // Original role before any role switches (for returning to home role)
}

/**
 * Check if user has a specific role
 * @param user - User object
 * @param role - Role to check for
 * @returns true if user has the role
 */
export function hasRole(user: User | null | undefined, role: UserRole): boolean {
  if (!user) return false;

  // Check roles array first (new system)
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.includes(role);
  }

  // Fallback to legacy single role field
  if (user.role === role) {
    return true;
  }

  // Handle legacy boolean flags
  if (role === 'systemAdmin' && user.isAdmin) return true;
  if (role === 'companyAdmin' && user.isCompanyAdmin) return true;
  if (role === 'billingAdmin' && user.isBillingAdmin) return true;

  return false;
}

/**
 * Check if user has any of the specified roles
 * @param user - User object
 * @param roles - Array of roles to check for
 * @returns true if user has at least one of the roles
 */
export function hasAnyRole(user: User | null | undefined, roles: UserRole[]): boolean {
  if (!user) return false;
  return roles.some(role => hasRole(user, role));
}

/**
 * Check if user has all of the specified roles
 * @param user - User object
 * @param roles - Array of roles to check for
 * @returns true if user has all the roles
 */
export function hasAllRoles(user: User | null | undefined, roles: UserRole[]): boolean {
  if (!user) return false;
  return roles.every(role => hasRole(user, role));
}

/**
 * Check if user is a firm user (consultant, cfAdmin, billingAdmin, or bdRep)
 * These users should see the ConsultantDashboard
 */
export function isFirmUser(user: User | null | undefined): boolean {
  return hasAnyRole(user, ['consultant', 'cfAdmin', 'billingAdmin', 'bdRep']);
}

/**
 * Check if user is a client user (companyAdmin or user)
 * These users should see the ClientDashboard
 */
export function isClientUser(user: User | null | undefined): boolean {
  // If they're a firm user, they're not a client user
  if (isFirmUser(user)) return false;
  
  // Check for client roles (includes legacy 'companyUser' and 'client')
  return hasAnyRole(user, ['companyAdmin', 'companyUser', 'user', 'client']);
}

/**
 * Check if user is System Admin
 */
export function isSystemAdmin(user: User | null | undefined): boolean {
  return hasRole(user, 'systemAdmin');
}

/**
 * Get the user's active view role — the role that determines which UI experience
 * to render. This is distinct from isSystemAdmin() which checks base privilege.
 * 
 * Priority: user.activeRole > user.role > 'user'
 */
export function getActiveViewRole(user: User | null | undefined): string {
  if (!user) return 'user';
  return user.activeRole || user.role || 'user';
}

/**
 * Check if the user's active view role is systemAdmin.
 * Returns false when a system admin is testing another role (e.g. cfAdmin).
 * Use isSystemAdmin() for authorization, this for view selection.
 */
export function isActiveViewSystemAdmin(user: User | null | undefined): boolean {
  return getActiveViewRole(user) === 'systemAdmin' || getActiveViewRole(user) === 'system_admin';
}

/**
 * Check if user's active view role is a firm role (for view selection).
 * This respects activeRole, unlike isFirmUser() which checks base roles.
 */
export function isActiveViewFirmUser(user: User | null | undefined): boolean {
  const role = getActiveViewRole(user);
  return ['consultant', 'cfAdmin', 'billingAdmin', 'bdRep', 'Engagement Lead', 'Engineer', 'leadership_marketing'].includes(role);
}

/**
 * Check if user's active view role is billing admin only (for view selection).
 */
export function isActiveViewBillingAdminOnly(user: User | null | undefined): boolean {
  return getActiveViewRole(user) === 'billingAdmin';
}

/**
 * Get display name for a role (for UI)
 */
export function getRoleDisplayName(role: UserRole): string {
  const displayNames: Record<UserRole, string> = {
    systemAdmin: 'System Admin',
    consultant: 'Consultant',
    cfAdmin: 'Firm Admin',
    billingAdmin: 'Billing Admin',
    bdRep: 'Business Development Rep',
    companyAdmin: 'Client Engagement Lead',
    user: 'Client Team Member',
  };
  return displayNames[role] || role;
}

/**
 * Get all roles for a user as an array
 */
export function getUserRoles(user: User | null | undefined): UserRole[] {
  if (!user) return [];

  // If roles array exists, use it
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles as UserRole[];
  }

  // Fallback: construct roles array from legacy fields
  const roles: UserRole[] = [];
  
  if (user.role) {
    roles.push(user.role as UserRole);
  }
  
  if (user.isAdmin) {
    roles.push('systemAdmin');
  }
  
  if (user.isCompanyAdmin) {
    roles.push('companyAdmin');
  }
  
  if (user.isBillingAdmin) {
    roles.push('billingAdmin');
  }

  // Remove duplicates
  return Array.from(new Set(roles));
}

/**
 * Get primary role for display purposes
 * Returns the "highest" role in the hierarchy
 */
export function getPrimaryRole(user: User | null | undefined): UserRole | null {
  if (!user) return null;

  const roles = getUserRoles(user);
  
  // Role hierarchy (highest to lowest)
  const hierarchy: UserRole[] = [
    'systemAdmin',
    'cfAdmin',
    'billingAdmin',
    'consultant',
    'bdRep',
    'companyAdmin',
    'user',
  ];

  for (const role of hierarchy) {
    if (roles.includes(role)) {
      return role;
    }
  }

  return null;
}

/**
 * Check if user can perform an action based on their roles
 * This is a helper for permission checking
 */
export function canPerformAction(
  user: User | null | undefined,
  action: string,
  context?: { companyId?: string; firmId?: string }
): boolean {
  if (!user) return false;

  // System admins can do anything
  if (isSystemAdmin(user)) return true;

  // Define action permissions
  switch (action) {
    case 'viewAllClients':
      return isFirmUser(user);
    
    case 'createClient':
      return hasAnyRole(user, ['consultant', 'cfAdmin', 'bdRep']);
    
    case 'editClient':
      return hasAnyRole(user, ['consultant', 'cfAdmin']);
    
    case 'createEngagement':
      return hasAnyRole(user, ['consultant', 'cfAdmin']);
    
    case 'viewBilling':
      return hasRole(user, 'billingAdmin') || hasRole(user, 'cfAdmin');
    
    case 'manageBilling':
      return hasRole(user, 'billingAdmin');
    
    case 'manageFirmSettings':
      return hasRole(user, 'cfAdmin');
    
    case 'manageCompanyUsers':
      return hasRole(user, 'companyAdmin');
    
    case 'answerDiscovery':
      return isClientUser(user) || isFirmUser(user);
    
    case 'uploadDocuments':
      return hasAnyRole(user, ['consultant', 'cfAdmin', 'companyAdmin']);
    
    default:
      return false;
  }
}